import "./ab-select.style.scss";
import {
  AbSelectChangeDetail,
  AbSelectOption,
  AbSelectSearchDetail
} from "@/scripts/components/ab-select/ab-select.types";
import { buildDom, buildListItems } from "@/scripts/components/ab-select/ab-select-functions.utils";

export function defineAbSelectElement() {
  const TAG_NAME = "ab-select";

  if (customElements.get(TAG_NAME)) return;

  class AbSelectElement extends HTMLElement {

    #initialized = false;
    #options: AbSelectOption[] = [];

    // DOM
    #inputEl!: HTMLInputElement;
    #listEl!: HTMLDivElement;

    #isDropdown = false;

    #onInput = (event: Event): void => {
      const value = (event.target as HTMLInputElement).value ?? "";
      this.dispatchEvent(
        new CustomEvent<AbSelectSearchDetail>("ab-select-search", {
          detail: { term: value },
          bubbles: true,
        }),
      );
    };

    #onFocus = (): void => {
      if (!this.#isDropdown) return;
      this.#openPanel();
    };

    #onDocumentClick = (event: MouseEvent): void => {
      if (!this.#isDropdown) return;
      if (!this.contains(event.target as Node)) {
        this.#closePanel();
      }
    };

    #onClickItem = (event: Event): void => {
      event.stopPropagation();
      const target = (event.target as HTMLElement).closest<HTMLElement>("[data-index]");
      if (!target) return;

      const indexAttr = target.getAttribute("data-index");
      if (indexAttr == null) return;

      const index = Number(indexAttr);
      if (Number.isNaN(index) || index < 0 || index >= this.#options.length) {
        return;
      }

      const option = this.#options[index];

      if (this.#isDropdown) {
        this.#closePanel();
      }

      this.dispatchEvent(
        new CustomEvent<AbSelectChangeDetail>("ab-select-change", {
          detail: { option },
          bubbles: true,
        }),
      );
    };

    #openPanel(): void {
      this.classList.add("ab-select--open");
    }

    #closePanel(): void {
      this.classList.remove("ab-select--open");
    }

    constructor() {
      super();
    }

    get options(): AbSelectOption[] {
      return this.#options;
    }

    set options(value: AbSelectOption[]) {
      this.#options = Array.isArray(value) ? value : [];
      this.#renderOptions();
    }

    clear(): void {
      if (this.#inputEl) {
        this.#inputEl.value = "";
      }
      this.#closePanel();
    }

    connectedCallback(): void {
      if (this.#initialized) {
        this.#bindEvents();
        return;
      }
      this.#initialized = true;
      this.#renderBase();
      this.#cacheDom();
      this.#bindEvents();
      this.#renderOptions();
    }

    disconnectedCallback(): void {
      this.#unbindEvents();
    }

    static get observedAttributes(): string[] {
      return [];
    }

    #renderBase(): void {
      this.classList.add("ab-select");

      const fontSize = this.getAttribute("font-size");
      if(fontSize?.length && ['sm', 'md', 'lg'].includes(fontSize)) {
        this.classList.add(`ab-select--font-size-${fontSize}`);
      }

      const templatePopup = this.hasAttribute("template-popup");
      if (templatePopup) {
        this.classList.add("ab-select--popup");
      }

      this.#isDropdown = this.hasAttribute("dropdown");
      if (this.#isDropdown) {
        this.classList.add("ab-select--dropdown");
      }

      const spacing = this.getAttribute("spacing");
      if(spacing?.length && ['sm', 'md', 'lg'].includes(spacing)) {
        this.classList.add(`ab-select--spacing-${spacing}`);
      }

      const hasShadow = this.hasAttribute("has-shadow");
      if (hasShadow) {
        this.classList.add("ab-select--shadow");
      }
      this.innerHTML = buildDom();
    }

    #cacheDom(): void {
      this.#inputEl = this.querySelector<HTMLInputElement>(".ab-select__input")!;
      this.#listEl = this.querySelector<HTMLDivElement>(".ab-select__list")!;
    }

    #bindEvents(): void {
      this.#inputEl.addEventListener("input", this.#onInput);
      this.#inputEl.addEventListener("focus", this.#onFocus);
      this.#listEl.addEventListener("click", this.#onClickItem);
      document.addEventListener("click", this.#onDocumentClick);
    }

    #unbindEvents(): void {
      if (this.#inputEl) {
        this.#inputEl.removeEventListener("input", this.#onInput);
        this.#inputEl.removeEventListener("focus", this.#onFocus);
      }
      if (this.#listEl) {
        this.#listEl.removeEventListener("click", this.#onClickItem);
      }
      document.removeEventListener("click", this.#onDocumentClick);
    }

    #renderOptions(): void {
      if (!this.#listEl) return;
      this.#listEl.innerHTML = buildListItems(this.#options);
    }

  }

  customElements.define(TAG_NAME, AbSelectElement);
}
